/**
 * Formats du fil des ventes — calcul PUR (0 React, 0 réseau), testé.
 *
 *   formatMontant(3499, "EUR")  → "34,99 €"
 *   formatMontant(732700, "USD") → "7 327 $US"   (entier : pas de décimales)
 *
 * Les espaces sont des fines insécables (U+202F), comme Kayla les écrit.
 */
import type { GroupeDeStatut, Vente } from "./types";

/** U+202F, l'espace fine insécable — par son code, pour que la source reste en ASCII. */
const FINE = String.fromCharCode(0x202f);
/** Les espaces qu'Intl pose dans un montant (insécable, fine, ordinaire). */
const ESPACES = new RegExp(`[${String.fromCharCode(0xa0)}${FINE} ]`, "g");

export function formatMontant(centimes: number, devise: string): string {
  const valeur = centimes / 100;
  const entier = Number.isInteger(valeur);
  let texte: string;
  try {
    texte = new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: devise,
      minimumFractionDigits: entier ? 0 : 2,
      maximumFractionDigits: entier ? 0 : 2,
    }).format(valeur);
  } catch {
    // Devise inconnue d'Intl : le nombre, puis le code tel quel.
    texte = `${new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: entier ? 0 : 2,
      maximumFractionDigits: entier ? 0 : 2,
    }).format(valeur)} ${devise}`;
  }
  return texte.replace(ESPACES, FINE);
}

/** La date qui classe et date la ligne : le paiement s'il est là, sinon la
 *  création — la règle de `orderRowDate` (table des commandes). */
export function dateDeLaVente(vente: Pick<Vente, "created_at" | "payment_processed_at">): string {
  const paye = vente.payment_processed_at;
  if (paye && !Number.isNaN(new Date(paye).getTime())) return paye;
  return vente.created_at;
}

/** « 15:55 » — l'heure, dans le fuseau demandé (défaut : celui du navigateur). */
export function formatHeure(dateIso: string, fuseau?: string): string {
  return new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: fuseau,
  }).format(new Date(dateIso));
}

/** `AAAA-MM-JJ` du jour civil de `date` dans `fuseau` — la clé d'un groupe. */
export function cleDuJour(date: Date, fuseau?: string): string {
  const parts = new Intl.DateTimeFormat("fr-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: fuseau,
  }).formatToParts(date);
  const lire = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${lire("year")}-${lire("month")}-${lire("day")}`;
}

/** « Aujourd'hui », « Hier », sinon « Samedi 25 octobre » (l'année seulement si elle diffère). */
export function libelleDuJour(cle: string, maintenant: Date, fuseau?: string): string {
  const aujourdhui = cleDuJour(maintenant, fuseau);
  if (cle === aujourdhui) return "Aujourd'hui";
  const hier = cleDuJour(new Date(maintenant.getTime() - 86_400_000), fuseau);
  if (cle === hier) return "Hier";
  const [a, m, j] = cle.split("-").map(Number);
  const date = new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, j ?? 1, 12));
  const memeAnnee = cle.slice(0, 4) === aujourdhui.slice(0, 4);
  const texte = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: memeAnnee ? undefined : "numeric",
    timeZone: "UTC",
  }).format(date);
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

// ─── Statut ────────────────────────────────────────────────────────────────────

/** Ce que dit la pastille : le groupe du backend, sauf `refunded` qui prime. */
export type StatutAffiche = GroupeDeStatut | "refunded";

export function statutAffiche(vente: Pick<Vente, "status_group" | "status">): StatutAffiche {
  return vente.status === "refunded" ? "refunded" : vente.status_group;
}

export const LIBELLE_STATUT: Record<StatutAffiche, string> = {
  paid: "Payé",
  pending: "En attente",
  cancelled: "Annulé",
  refunded: "Remboursé",
};

/** Une vente compte dans le total du jour si l'argent est arrivé et resté. */
export function estEncaissee(vente: Pick<Vente, "status_group" | "status">): boolean {
  return statutAffiche(vente) === "paid";
}

/** Qui a vendu, pour la ligne : « Cyrano », le chatteur (la partie avant l'@), ou rien. */
export function libelleVendeur(vente: Pick<Vente, "sender_source" | "operator_email">): string | null {
  if (vente.sender_source === "cyrano") return "Cyrano";
  if (vente.operator_email) return vente.operator_email.split("@")[0] || null;
  if (vente.sender_source === "operator") return "Chatteur";
  return vente.sender_source ?? null;
}

// ─── Groupes ───────────────────────────────────────────────────────────────────

export interface JourDeVentes {
  cle: string;
  libelle: string;
  ventes: Vente[];
  /** Le total encaissé du jour, une entrée par devise, la plus grosse d'abord :
   *  « 44,98 € + 9,99 $US ». Les attentes, annulations et remboursements ne
   *  comptent pas. */
  totaux: { devise: string; centimes: number }[];
}

/** Groupe par jour civil, du plus récent au plus ancien ; dans un jour, du plus récent au plus ancien. */
export function grouperParJour(ventes: readonly Vente[], maintenant: Date, fuseau?: string): JourDeVentes[] {
  const triees = [...ventes].sort((x, y) => dateDeLaVente(y).localeCompare(dateDeLaVente(x)));
  const jours = new Map<string, JourDeVentes>();
  for (const v of triees) {
    const cle = cleDuJour(new Date(dateDeLaVente(v)), fuseau);
    let jour = jours.get(cle);
    if (!jour) {
      jour = { cle, libelle: libelleDuJour(cle, maintenant, fuseau), ventes: [], totaux: [] };
      jours.set(cle, jour);
    }
    jour.ventes.push(v);
  }
  for (const jour of jours.values()) {
    const parDevise = new Map<string, number>();
    for (const v of jour.ventes) {
      const devise = v.currency.toUpperCase();
      parDevise.set(devise, (parDevise.get(devise) ?? 0) + (estEncaissee(v) ? v.amount_cents : 0));
    }
    const tous = [...parDevise.entries()]
      .map(([devise, centimes]) => ({ devise, centimes }))
      .sort((x, y) => y.centimes - x.centimes);
    // Une devise qui n'a rien encaissé n'a rien à dire — sauf s'il n'y a qu'elle.
    const nonNuls = tous.filter((t) => t.centimes !== 0);
    jour.totaux = nonNuls.length > 0 ? nonNuls : tous.slice(0, 1);
  }
  return [...jours.values()];
}

/** « 44,98 € + 9,99 $US » — les devises l'une après l'autre ; un total négatif
 *  (si un jour le backend en produit) se lit avec un vrai signe moins. */
export function formatTotaux(totaux: readonly { devise: string; centimes: number }[]): string {
  return totaux
    .map((t, i) => {
      const montant = formatMontant(Math.abs(t.centimes), t.devise);
      if (i === 0) return t.centimes < 0 ? `−${FINE}${montant}` : montant;
      return `${t.centimes < 0 ? "−" : "+"} ${montant}`;
    })
    .join(" ");
}

/** « 15 % », « 12,5 % » — un taux, fine insécable avant le signe. */
export function formatTaux(pourcent: number): string {
  return `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(pourcent)}${FINE}%`;
}

const NOMS_DE_PAYS = (() => {
  try {
    return new Intl.DisplayNames(["fr"], { type: "region" });
  } catch {
    return null;
  }
})();

/** « France » pour `FR` ; le code tel quel si le navigateur ne le connaît pas. */
export function nomDuPays(code: string): string {
  const maj = code.toUpperCase();
  try {
    return NOMS_DE_PAYS?.of(maj) ?? maj;
  } catch {
    return maj;
  }
}
